import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/client';
import type { ContentItem, ContentPage, PreviewTicket, TagCount } from '../api/types';
import { useApi, useDebounced, useMutation } from '../lib/use-api';
import { formatDateTime, languageName } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { PreviewDialog } from '../components/PreviewDialog';
import { TagEditor } from '../components/TagEditor';
import { ExportDialog } from '../components/ExportDialog';
import { ActionMenu } from '../components/ActionMenu';

const PAGE_SIZE = 25;

/**
 * Thư viện học liệu — plan §5.1.
 *
 * Tìm kiếm gõ không dấu ra bài có dấu: cột `title_ascii` ở tầng DB lo việc đó
 * (§4.5), phía này chỉ cần gửi nguyên chuỗi người dùng gõ.
 */
export function ContentLibraryPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const location = useLocation();
  const navigate = useNavigate();

  const [keyword, setKeyword] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [previewing, setPreviewing] = useState<ContentItem | undefined>(undefined);
  const [editingTags, setEditingTags] = useState<ContentItem | undefined>(undefined);
  const [exporting, setExporting] = useState<ContentItem | undefined>(undefined);

  const debouncedKeyword = useDebounced(keyword);

  const query = useMemo(
    () => ({
      q: debouncedKeyword,
      tags: selectedTags.join(','),
      limit: PAGE_SIZE,
      offset,
    }),
    [debouncedKeyword, selectedTags, offset],
  );

  const contents = useApi<ContentPage>('/api/admin/content', { query });
  const tags = useApi<{ items: TagCount[] }>('/api/admin/tags');

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      const data = event.data as { action?: string; type?: string } | undefined;
      if (data && (data.action === 'h5p_saved' || data.type === 'H5P_SAVED')) {
        contents.reload();
        tags.reload();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [contents, tags]);

  const previewTicket = useMutation<ContentItem, PreviewTicket>(
    useCallback(
      (item: ContentItem) =>
        apiFetch<PreviewTicket>(`/api/admin/content/${item.contentId}/preview-ticket`, {
          method: 'POST',
          body: { language: item.language },
        }),
      [],
    ),
  );

  const [ticket, setTicket] = useState<PreviewTicket | undefined>(undefined);

  const openPreview = async (item: ContentItem): Promise<void> => {
    const issued = await previewTicket.run(item);
    if (!issued) return;
    setTicket(issued);
    setPreviewing(item);
  };

  useEffect(() => {
    const previewContentId = location.state?.previewContentId as string | undefined;
    if (previewContentId && contents.data) {
      const item = contents.data.items.find((i) => i.contentId === previewContentId);
      if (item) {
        navigate(location.pathname, { replace: true, state: {} });
        void openPreview(item);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, contents.data, navigate]);

  /**
   * Vé soạn bài — mở trang editor của h5p-core trong tab mới.
   *
   * Tab mới không mang được header `Authorization`, nên danh tính đi bằng vé
   * ký ngắn hạn giống hệt vé xem thử. Trước đây không có nút nào: người dùng
   * phải tự gõ `localhost:3001/new` vào thanh địa chỉ, và trang đó chạy dưới
   * `dev-tenant` bất kể ai đang đăng nhập.
   */
  const authorTicket = useMutation<ContentItem | undefined, { url: string }>(
    useCallback(
      (item: ContentItem | undefined) =>
        apiFetch<{ url: string }>(
          item ? `/api/admin/content/${item.contentId}/author-ticket` : '/api/admin/author-ticket',
          { method: 'POST', body: { language: item?.language ?? locale } },
        ),
      [locale],
    ),
  );

  const openEditor = async (item?: ContentItem): Promise<void> => {
    const issued = await authorTicket.run(item);
    // `noopener` bắt buộc: thiếu nó thì trang mở ra giữ được `window.opener`
    // và đổi được URL của web-admin bằng JS.
    if (issued) window.open(issued.url, '_blank', 'noopener,noreferrer');
  };

  const toggleTag = (tag: string): void => {
    // Đổi bộ lọc thì về trang đầu: giữ nguyên offset sau khi lọc sẽ cho ra
    // trang trắng và người dùng tưởng không có bài nào.
    setOffset(0);
    setSelectedTags((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag],
    );
  };

  return (
    <section>
      <h1>{t('library.heading')}</h1>

      <div className="toolbar">
        <label className="search-field">
          <span className="visually-hidden">{t('common:action.search')}</span>
          <input
            type="search"
            value={keyword}
            placeholder={t('library.searchPlaceholder')}
            onChange={(event) => {
              setKeyword(event.target.value);
              setOffset(0);
            }}
          />
        </label>
        <p className="hint">{t('library.searchHint')}</p>
        <button type="button" disabled={authorTicket.pending} onClick={() => void openEditor()}>
          {t('library.createContent')}
        </button>
      </div>

      {authorTicket.error ? <ErrorNotice error={authorTicket.error} /> : null}

      {tags.data && tags.data.items.length > 0 ? (
        <div className="tag-filter">
          <span className="tag-filter__label">{t('library.filterByTag')}</span>
          {tags.data.items.map((item) => (
            <button
              key={item.tag}
              type="button"
              className={selectedTags.includes(item.tag) ? 'chip chip--active' : 'chip'}
              aria-pressed={selectedTags.includes(item.tag)}
              onClick={() => toggleTag(item.tag)}
            >
              {item.tag} <span className="chip__count">{item.count}</span>
            </button>
          ))}
          {selectedTags.length > 0 ? (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setSelectedTags([]);
                setOffset(0);
              }}
            >
              {t('library.clearTagFilter')}
            </button>
          ) : null}
        </div>
      ) : null}

      {contents.loading ? <Loading /> : null}
      {contents.error ? <ErrorNotice error={contents.error} onRetry={contents.reload} /> : null}
      {previewTicket.error ? <ErrorNotice error={previewTicket.error} /> : null}

      {contents.data && !contents.loading ? (
        contents.data.items.length === 0 ? (
          <EmptyNotice message={t('library.empty')} />
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('library.column.title')}</th>
                  <th scope="col">{t('library.column.language')}</th>
                  <th scope="col">{t('library.column.contentType')}</th>
                  <th scope="col">{t('library.column.tags')}</th>
                  <th scope="col">{t('library.column.updatedAt')}</th>
                  <th scope="col">{t('library.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {contents.data.items.map((item) => (
                  <tr key={item.contentId}>
                    <td>{item.title}</td>
                    <td>{languageName(item.language, locale)}</td>
                    <td className="mono">{item.mainLibrary}</td>
                    <td>
                      {item.tags.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        item.tags.map((tag) => (
                          <span key={tag} className="chip chip--static">
                            {tag}
                          </span>
                        ))
                      )}
                    </td>
                    <td>{formatDateTime(item.updatedAt, locale)}</td>
                    <td className="actions">
                      <ActionMenu
                        items={[
                          {
                            key: 'edit',
                            label: t('common:action.edit'),
                            disabled: authorTicket.pending,
                            icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                              </svg>
                            ),
                            onClick: () => void openEditor(item),
                          },
                          {
                            key: 'preview',
                            label: t('common:action.preview'),
                            icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            ),
                            onClick: () => void openPreview(item),
                          },
                          {
                            key: 'tags',
                            label: t('library.editTags'),
                            icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                                <line x1="7" y1="7" x2="7.01" y2="7" />
                              </svg>
                            ),
                            onClick: () => setEditingTags(item),
                          },
                          {
                            key: 'export',
                            label: t('common:action.export'),
                            icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            ),
                            onClick: () => setExporting(item),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              total={contents.data.total}
              limit={contents.data.limit}
              offset={contents.data.offset}
              onChange={setOffset}
            />
          </>
        )
      ) : null}

      {previewing && ticket ? (
        <PreviewDialog
          title={previewing.title}
          ticket={ticket}
          contentId={previewing.contentId}
          onClose={() => {
            setPreviewing(undefined);
            setTicket(undefined);
          }}
        />
      ) : null}

      {exporting ? (
        <ExportDialog content={exporting} onClose={() => setExporting(undefined)} />
      ) : null}

      {editingTags ? (
        <TagEditor
          content={editingTags}
          onClose={() => setEditingTags(undefined)}
          onSaved={() => {
            setEditingTags(undefined);
            contents.reload();
            tags.reload();
          }}
        />
      ) : null}
    </section>
  );
}
